import { makeAutoObservable } from "mobx";
import httpClient from "@/config/httpClient";
import { detectBrowserLanguage } from "@/i18n";
import { endpoints } from "@/utils/common/requests/endpoints";
import construct from "../utils/construct";
import persist from "../utils/persist";

// eslint-disable-next-line
let _rootStore;

/**
 * @typedef {Object} NavbarState
 * @property {number} width
 * @property {boolean} opened
 */

/**
 * @typedef {Object} ThumbBurgerState
 * @property {boolean} show
 * @property {"left" | "right"} position
 */

const defaultProperties = {
	language: detectBrowserLanguage(),
	theme: "dark",
	header: {
		height: 70,
	},
	navbar: {
		width: 0,
		opened: true,
	},
	thumbBurger: {
		show: true,
		position: /** @type {"left" | "right"} */ ("left"),
	},
};

export default class UiStore {
	/** @type {string} */
	language = defaultProperties.language;
	/** @type {string} */
	theme = defaultProperties.theme;
	/** @type {{ height: number }} */
	header = defaultProperties.header;
	/** @type {NavbarState} */
	navbar = defaultProperties.navbar;
	/** @type {ThumbBurgerState} */
	thumbBurger = defaultProperties.thumbBurger;

	constructor(rootStore) {
		construct(this, defaultProperties);
		_rootStore = rootStore;

		makeAutoObservable(this);
		persist(this, "uiStore");

		// Migracion es-ES -> es (usuarios existentes)
		if (typeof this.language === "string" && this.language.includes("-")) {
			this.language = this.language.split("-")[0];
		}
	}

	reset() {
		construct(this, defaultProperties);
	}

	/** * @param {"light" | "dark"} theme */
	toggleTheme = (theme) => {
		// Forzamos tema
		if (theme) {
			if (!["light", "dark"].includes(theme)) {
				return console.warn(`[setTheme] ${theme} is not a valid theme`);
			}

			this.theme = theme;

			// Cambiamos de un tema al otro
		} else {
			if (this.theme === "light") {
				this.theme = "dark";
			} else {
				this.theme = "light";
			}
		}
	};

	/**
	 * Devuelve si estamos en tema oscuro o no.
	 * @returns {boolean} true si estamos en tema oscuro, false si no.
	 */
	isDarkTheme = () => this.theme === "dark";

	getMobileNavbarState = () => this.navbar.opened;

	getIsNavbarOpened = () => this.navbar.opened;

	toggleMobileNavbar = () => {
		const nextState = !this.navbar.opened;
		this.navbar.opened = nextState;
		// this.navbar.open = nextState;
	};
	closeMobileNavbar = () => {
		this.navbar.opened = false;
	};
	openMobileNavbar = () => {
		this.navbar.opened = true;
	};

	getDesktopNavbarState = () => this.navbar.opened;

	toggleDesktopNavbar = () => {
		this.navbar.opened = !this.navbar.opened;
	};

	closeNavbar = () => {
		this.navbar.opened = false;
	};
	openNavbar = () => {
		this.navbar.opened = true;
	};
	toggleNavbar = () => {
		const nextState = !this.navbar.opened;
		this.navbar.opened = nextState;
	};

	/**
	 * @param {"left" | "right"} position
	 */
	setThumbBurgerPosition = (position) => {
		this.thumbBurger.position = position;
	};

	toggleThumbBurgerPosition = () => {
		if (this.thumbBurger.position === "left") {
			this.setThumbBurgerPosition("right");
		} else {
			this.setThumbBurgerPosition("left");
		}
	};

	/**
	 * @param {string} lang
	 * @param {Object} [options]
	 * @param {boolean} [options.persist=true] Si false, no persiste el idioma en settings (backend)
	 */
	setLanguage = (lang, { persist: persistToSettings = true } = {}) => {
		this.language = lang;
		// Activa Lingui + dayjs en runtime (import dinamico para evitar ciclo)
		import("@lingui/core").then(({ i18n }) => i18n.activate(lang));
		import("dayjs").then(({ default: dayjs }) => dayjs.locale(lang));

		// Fire-and-forget: sincroniza el idioma con el setting del backend
		if (persistToSettings && _rootStore?.userStore?.isAuthenticated) {
			httpClient
				.patch(endpoints.settings.UPDATE(), { fields: { opt_language: lang } })
				.catch(() => {});
		}
	};
}
